import React from 'react';

interface AdminSideMenuProps {
  selected: string;
  onSelect: (section: string) => void;
  userRoles?: string[];
}

function hasRole(roles: string[] | undefined, role: string): boolean {
  if (!roles || !Array.isArray(roles)) return false;
  return roles.map(r => r.toLowerCase()).includes(role.toLowerCase());
}

function getUserSports(roles: string[] | undefined): string[] {
  if (!roles || !Array.isArray(roles)) return [];
  return roles
    .filter(r => typeof r === 'string' && r.startsWith('sports_admin_'))
    .map(r => r.replace('sports_admin_', '').toLowerCase());
}

function AdminSideMenu({ selected, onSelect, userRoles }: AdminSideMenuProps) {
  // Determine if user can access sport pages (admin or sports admin, but NOT trustee)
  const canAccessSportPages = hasRole(userRoles, 'admin') || getUserSports(userRoles).length > 0;

  // Determine if user can access mail (admin, trustee, or sports admin)
  const canAccessMail = hasRole(userRoles, 'admin') ||
                        hasRole(userRoles, 'trustee') ||
                        getUserSports(userRoles).length > 0;

  // Header height is 4rem (minHeight in Header), so top is 0 and height is 100vh minus 4rem
  return (
    <nav className="fixed left-0 top-[4rem] h-[calc(100vh-4rem)] w-56 bg-white shadow-lg flex flex-col gap-4 py-8 px-4 z-20">
      <button
        className={`text-lg font-semibold py-2 px-4 rounded-md text-left transition-colors ${selected === 'members' ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100 text-gray-800'}`}
        onClick={() => onSelect('members')}
      >
        Members
      </button>
      {canAccessSportPages && (
        <>
          <button
            className={`text-lg font-semibold py-2 px-4 rounded-md text-left transition-colors ${selected === 'sport' ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100 text-gray-800'}`}
            onClick={() => onSelect('sport')}
          >
            Sport
          </button>
          <button
            className={`text-lg font-semibold py-2 px-4 rounded-md text-left transition-colors ${selected === 'sportmembers' ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100 text-gray-800'}`}
            onClick={() => onSelect('sportmembers')}
          >
            Sport Members
          </button>
          <button
            className={`text-lg font-semibold py-2 px-4 rounded-md text-left transition-colors ${selected === 'grades' ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100 text-gray-800'}`}
            onClick={() => onSelect('grades')}
          >
            Grade Management
          </button>
          <button
            className={`text-lg font-semibold py-2 px-4 rounded-md text-left transition-colors ${selected === 'sportroles' ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100 text-gray-800'}`}
            onClick={() => onSelect('sportroles')}
          >
            Sport Roles
          </button>
        </>
      )}
      {/* Mail: available for admin, trustee, or sports admin */}
      {canAccessMail ? (
        <button
          className={`text-lg font-semibold py-2 px-4 rounded-md text-left transition-colors ${selected === 'mail' ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100 text-gray-800'}`}
          onClick={() => onSelect('mail')}
        >
          Mail
        </button>
      ) : null}
      {/* Settings and Audit Login: admin only */}
      {hasRole(userRoles, 'admin') ? (
        <>
          <button
            className={`text-lg font-semibold py-2 px-4 rounded-md text-left transition-colors ${selected === 'settings' ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100 text-gray-800'}`}
            onClick={() => onSelect('settings')}
          >
            Settings
          </button>
          <button
            className={`text-lg font-semibold py-2 px-4 rounded-md text-left transition-colors ${selected === 'auditlog' ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100 text-gray-800'}`}
            onClick={() => onSelect('auditlog')}
          >
            Audit Log
          </button>
          <button
            className={`text-lg font-semibold py-2 px-4 rounded-md text-left transition-colors ${selected === 'auditlogin' ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100 text-gray-800'}`}
            onClick={() => onSelect('auditlogin')}
          >
            Audit Login
          </button>
          <button
            className={`text-lg font-semibold py-2 px-4 rounded-md text-left transition-colors ${selected === 'maillog' ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100 text-gray-800'}`}
            onClick={() => onSelect('maillog')}
          >
            Mail Log
          </button>
        </>
      ) : null}
    </nav>
  );
};

export default AdminSideMenu;
